import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SessionService } from './session.service';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'touchstart', 'scroll', 'wheel'];

@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {
  private subscription?: Subscription;

  constructor(
    private ngZone: NgZone,
    private sessionService: SessionService
  ) {}

  /**
   * Starts (or restarts, if already running) the idle timer. Activity
   * listeners are attached outside Angular's zone so mousemove/scroll don't
   * trigger change detection on every event; only the eventual timeout
   * re-enters the zone.
   */
  start(idleTimeoutMs: number): void {
    this.stop();

    this.ngZone.runOutsideAngular(() => {
      const activityEvents$ = merge(
        ...ACTIVITY_EVENTS.map(eventName => fromEvent(window, eventName, { passive: true }))
      );
      const activity$ = merge(activityEvents$, timer(0));

      this.subscription = activity$
        .pipe(switchMap(() => timer(idleTimeoutMs)))
        .subscribe(() => this.ngZone.run(() => this.onIdleTimeout()));
    });
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  private onIdleTimeout(): void {
    this.stop();
    this.sessionService.handleSessionTimeout();
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
